/**
 * Task D5 — request an inventory export, watch it, download it (routes 6, 7, 8).
 *
 * **The download is addressed by our `dl_` ref and named after it** (criterion
 * 22). The upstream's own file name embeds its job ids and account fragments,
 * which is exactly what §7 exists to keep out of the client; and an integer id
 * in a URL is both a leak and an enumeration invitation. So the path is
 * `/exports/{dl_…}/file` and the saved file is called after the same ref, with
 * only the extension borrowed from the descriptor.
 *
 * **The bytes come back through `apiStream`, which returns a `Blob` rather than
 * navigating.** A plain `<a href>` to the file route would leave the bearer
 * token out of the request — the token lives in module scope and is attached by
 * `apiClient`, never by the browser — so the download would 401. Handing the
 * caller a Blob also means the failure case is an error state on this page
 * instead of a browser tab showing raw JSON.
 *
 * Requesting an export is a write, so it carries an `Idempotency-Key` (§05.6).
 * The key is minted once per user gesture and **reused across retries of that
 * gesture**: if the first attempt reached the server before the connection
 * dropped, the replay returns the original export instead of asking the network
 * to generate the whole estate a second time.
 */

import type { ExportSummary } from '@rd/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';

import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Pagination } from '../../components/Pagination.js';
import { Skeleton } from '../../components/Skeleton.js';
import { Table, type Column } from '../../components/Table.js';
import { apiRequest, apiStream, newIdempotencyKey } from '../../lib/apiClient.js';
import { usePaginatedQuery } from '../../lib/paginated.js';

const EXPORTS_KEY = ['exports'] as const;

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

/**
 * What the browser saves the file as.
 *
 * **Our ref, never the upstream's file name.** The extension is the one piece
 * of the descriptor worth keeping — it is what makes the file open in a
 * spreadsheet rather than as unknown bytes — and it is pulled out with a
 * conservative pattern so nothing else in that string reaches the filesystem.
 */
function downloadName(row: ExportSummary): string {
  const extension = /\.([A-Za-z0-9]{1,8})$/.exec(row.fileName ?? '')?.[1];
  return extension === undefined ? row.exportRef : `${row.exportRef}.${extension}`;
}

export function ExportsPage(): ReactElement {
  const queryClient = useQueryClient();
  // Same contract as every other list: `page` and `pageSize` round-trip through
  // the URL, so a link to page 3 of the export history is a link.
  const query = usePaginatedQuery<ExportSummary>({ key: EXPORTS_KEY, path: '/exports' });

  /**
   * The key for the gesture currently in flight. Held in a ref rather than
   * minted inside `mutationFn` so that a retry of the same click reuses it;
   * cleared on success so the *next* click is a genuinely new operation
   * (§05.6).
   */
  const idempotencyKeyRef = useRef<string | null>(null);

  const request = useMutation<ExportSummary>({
    mutationFn: () => {
      idempotencyKeyRef.current ??= newIdempotencyKey();
      return apiRequest<ExportSummary>('/exports/inventory', {
        method: 'POST',
        body: {},
        idempotencyKey: idempotencyKeyRef.current,
      });
    },
    onSuccess: () => {
      idempotencyKeyRef.current = null;
      void queryClient.invalidateQueries({ queryKey: EXPORTS_KEY });
    },
  });

  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>(null);

  async function download(row: ExportSummary): Promise<void> {
    setDownloading(row.exportRef);
    setDownloadError(null);
    try {
      const blob = await apiStream(`/exports/${row.exportRef}/file`);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadName(row);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoked immediately: the click has already handed the blob to the
      // download manager, and an un-revoked object URL pins the whole file in
      // memory for the life of the document.
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      // An export that is still generating answers with an error code of its
      // own; `ErrorState` reads the copy for it rather than this page guessing.
      setDownloadError(err);
    } finally {
      setDownloading(null);
    }
  }

  const columns: Column<ExportSummary>[] = [
    {
      key: 'exportRef',
      header: 'Reference',
      render: (row) => <span className="font-mono text-sm">{row.exportRef}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <span className="text-body">{row.status ?? '—'}</span>,
    },
    {
      key: 'completedAt',
      header: 'Completed',
      render: (row) => <span className="text-muted">{formatWhen(row.completedAt)}</span>,
    },
    {
      key: 'download',
      header: 'File',
      render: (row) => (
        <Button
          variant="secondary"
          loading={downloading === row.exportRef}
          onClick={() => void download(row)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Exports"
        description="A spreadsheet of the whole SIM estate, generated by the network on request."
        actions={
          <Button loading={request.isPending} onClick={() => request.mutate()}>
            Request an export
          </Button>
        }
      />

      {request.isError ? (
        <div className="mb-6">
          <ErrorState error={request.error} onRetry={() => request.mutate()} />
        </div>
      ) : null}

      {downloadError !== null ? (
        <div className="mb-6">
          <ErrorState error={downloadError} />
        </div>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <>
          <Table
            caption="Inventory exports, newest first"
            columns={columns}
            rows={query.data.data}
            rowKey={(row) => row.exportRef}
            empty={
              <EmptyState
                title="No exports yet"
                body="Request one and it will appear here while the network generates it."
              />
            }
          />
          <Pagination meta={query.data.meta} onPageChange={query.setPage} label="Exports" />
        </>
      ) : null}
    </>
  );
}
